import { useEffect, useMemo, useState } from "react";
import ReportUrlModal from "../component/modal/ReportUrlModal";
import "../css/AdManagement.css";
import CampaignPageLayout from "../modules/campaign/components/CampaignPageLayout";
import NotificationModal from "../modules/campaign/components/NotificationModal";
import RefundModal from "../modules/campaign/components/RefundModal";
import adReviewDeleteConfig, {
  getPaymentLabel,
  getStatusLabel,
} from "../modules/campaign/configs/adReviewDelete.config";
import { useCampaignList } from "../modules/campaign/hooks/useCampaignList";
import { useFilters } from "../modules/campaign/hooks/useFilters";
import { useSelection } from "../modules/campaign/hooks/useSelection";
import {
  action,
  submitUrlRefund,
  saveMemo,
} from "../modules/campaign/services/campaignService";
import { download } from "../modules/campaign/services/downloadService";
import MemoModal from "../modules/campaign/components/MemoModal";
import { useMemoEdit } from "../modules/campaign/hooks/useMemoEdit";
import Layout from "./Layout";
import { toExternalUrl } from "../utils/url";

function AdReviewDelete() {
  const filters = useFilters(adReviewDeleteConfig.filters.default);
  const { items, summary, loading, refresh } = useCampaignList(
    adReviewDeleteConfig,
    filters.filter,
  );
  const selection = useSelection(items, adReviewDeleteConfig.table.keyField);
  const memoEdit = useMemoEdit({
    saveFn: saveMemo,
    onUpdate: () => refresh(),
  });

  const [isDownloading, setIsDownloading] = useState(false);

  // 팝업: selectedMetaId로 items에서 파생하여 항상 최신 데이터 표시
  const [selectedMetaId, setSelectedMetaId] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const selectedReview = useMemo(
    () => items.find((item) => item.adCampaignMetaId === selectedMetaId) || null,
    [items, selectedMetaId],
  );

  const [urlActionLoading, setUrlActionLoading] = useState({});

  const [notificationData, setNotificationData] = useState(null);
  const [refundModalData, setRefundModalData] = useState(null);
  const [reportMetaId, setReportMetaId] = useState(null);
  const [reportPlaceName, setReportPlaceName] = useState(null);

  // 알림톡 모달
  const openNotificationModal = (data) => {
    if (!data.adCampaignMetaId) {
      alert("캠페인 메타 ID가 없습니다.");
      return;
    }
    setNotificationData(data);
  };

  // 환불 모달 열기: 결제된 Meta의 환불 가능한 URL만 수집
  const handleRefundOpen = (selectedIds, allItems) => {
    const urlItems = allItems
      .filter((item) => selectedIds.includes(item.adCampaignMetaId) && (item.isPaid === 1 || item.isPaid === 2))
      .flatMap((item) =>
        (item.urls || [])
          .filter((url) => (url.remainingAmount ?? 0) > 0)
          .map((url) => ({
            adReviewDeleteId: url.adReviewDeleteId,
            reviewUrl: url.reviewUrl,
            unitCost: url.unitCost,
            refundedAmount: url.refundedAmount ?? 0,
            remainingAmount: url.remainingAmount ?? 0,
            userName: item.userName,
            userId: item.userId,
          })),
      );

    if (urlItems.length === 0) {
      alert("환불 가능한 항목이 없습니다.");
      return;
    }

    if (urlItems.length > 10) {
      alert("환불 처리는 한 번에 10건 이하만 선택할 수 있습니다.");
      return;
    }

    // 멱등성 키는 모달 세션 단위로 한 번만 생성 — 재시도 시 재사용
    const idempotencyKey = `review-refund-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    setRefundModalData({ items: urlItems, idempotencyKey });
  };

  // 환불 제출: URL 단위 신규 v2 API
  // 성공 시 responseData를 반환 → RefundModal에서 결과 스텝 표시
  // 실패 시 throw → RefundModal이 모달을 유지한 채 페이지에서 alert 처리
  const handleRefundSubmit = async (refundItems) => {
    try {
      const apiItems = refundItems.map(({ adReviewDeleteId, refundType, refundedAmount }) => {
        const item = { adReviewDeleteId, refundType };
        if (refundType === "PARTIAL") item.refundedAmount = refundedAmount;
        return item;
      });
      const responseData = await submitUrlRefund(apiItems, refundModalData?.idempotencyKey);
      selection.clearSelection();
      await refresh();
      return responseData; // RefundModal이 result 스텝 렌더링
    } catch (error) {
      console.error("환불 처리 오류:", error);
      // 400/422(검증 실패): 운영자가 금액을 수정해 재시도 → 키 재생성
      // 409(in-flight 중복)·429·5xx·네트워크 오류: 같은 요청의 재시도이므로 키 유지
      const isValidationError = error.status === 400 || error.status === 422;
      if (isValidationError) {
        const newKey = `review-refund-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        setRefundModalData((prev) => prev ? { ...prev, idempotencyKey: newKey } : prev);
      }
      alert(error.message || "환불 처리 중 오류가 발생했습니다.");
      throw error; // 모달 유지 (재시도 가능)
    }
  };

  // 팝업 내 URL별 접수/처리완료 액션
  const handleUrlAction = async (actionType, adReviewDeleteId) => {
    if (urlActionLoading[adReviewDeleteId]) return;
    setUrlActionLoading((prev) => ({ ...prev, [adReviewDeleteId]: true }));
    try {
      await action(adReviewDeleteConfig, actionType, {
        adReviewDeleteIds: [adReviewDeleteId],
      });
      await refresh();
    } catch (error) {
      alert(error.message || "처리 중 오류가 발생했습니다.");
    } finally {
      setUrlActionLoading((prev) => ({ ...prev, [adReviewDeleteId]: false }));
    }
  };

  const openReportModal = (adCampaignMetaId, placeName) => {
    if (!adCampaignMetaId) {
      alert("캠페인 메타 ID가 없습니다.");
      return;
    }
    setReportMetaId(adCampaignMetaId);
    setReportPlaceName(placeName || null);
  };

  // 알림톡 버튼 클릭 이벤트 위임
  useEffect(() => {
    const handleNotificationClick = (e) => {
      const button = e.target.closest('button[data-action="notification"]');
      if (button) {
        const adCampaignMetaId = Number(button.getAttribute("data-campaign-meta-id"));
        const userId = Number(button.getAttribute("data-user-id"));
        const productName = button.getAttribute("data-product-name") || "";
        const submittedAt = button.getAttribute("data-submitted-at") || "";
        const purchaseAmount = Number(button.getAttribute("data-purchase-amount")) || 0;
        openNotificationModal({ adCampaignMetaId, userId, productName, submittedAt, purchaseAmount });
      }
    };
    document.addEventListener("click", handleNotificationClick, true);
    return () => document.removeEventListener("click", handleNotificationClick, true);
  }, []);

  // 메모 버튼 클릭 이벤트 위임
  useEffect(() => {
    const handleMemoClick = (e) => {
      const button = e.target.closest('button[data-action="memo"]');
      if (button) {
        const adCampaignMetaId = Number(button.getAttribute("data-campaign-meta-id"));
        const currentMemo = button.getAttribute("data-memo") || null;
        memoEdit.openModal(adCampaignMetaId, currentMemo || null);
      }
    };
    document.addEventListener("click", handleMemoClick, true);
    return () => document.removeEventListener("click", handleMemoClick, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memoEdit.openModal]);

  const handleFilterChange = (name, value) => filters.updateFilter(name, value);
  const handleFilterApply = () => { filters.applyFilter(); selection.clearSelection(); };
  const handleFilterReset = () => { filters.resetFilter(); selection.clearSelection(); };
  const handleSelect = (id) => selection.toggleId(id);
  const handleSelectAll = (checked) => checked ? selection.selectAll() : selection.clearSelection();
  const handlePageChange = (page) => filters.setPage(page);

  const handleRowClick = (row) => {
    setSelectedMetaId(row.adCampaignMetaId);
    setIsPopupOpen(true);
  };

  const handleClosePopup = () => {
    setIsPopupOpen(false);
    setSelectedMetaId(null);
    setUrlActionLoading({});
  };

  const handleAction = async (actionType, payload, actionConfig) => {
    // 첨부자료 zip 다운로드
    if (actionType === "attachmentDownload") {
      const { rows } = payload;
      if (!rows || rows.length === 0) {
        alert("항목을 선택해주세요.");
        return;
      }
      try {
        setIsDownloading(true);
        await download({ download: adReviewDeleteConfig.attachmentDownload }, rows, []);
        alert("다운로드가 완료되었습니다.");
      } catch (error) {
        console.error("첨부자료 다운로드 오류:", error);
        alert(error.message || "다운로드 중 오류가 발생했습니다.");
      } finally {
        setIsDownloading(false);
      }
      return;
    }

    // submitted·complete: 선택한 Meta의 URL이 모두 refunded면 빈 배열 → early return
    if (Array.isArray(payload.adReviewDeleteIds) && payload.adReviewDeleteIds.length === 0) {
      alert("처리할 항목이 없습니다.");
      return;
    }

    try {
      const result = await action(adReviewDeleteConfig, actionType, payload);
      if (result.status === "success") {
        const messages = {
          submitted: "접수 상태가 변경되었습니다.",
          paid: "결제 완료 처리되었습니다.",
          complete: "처리 상태가 변경되었습니다.",
          delete: "삭제되었습니다.",
        };
        alert(messages[actionType] || "처리되었습니다.");
        selection.clearSelection();
        await refresh();
      } else {
        alert("처리 중 오류가 발생했습니다.");
      }
    } catch (error) {
      console.error("액션 처리 오류:", error);
      alert(error.message || "처리 중 오류가 발생했습니다.");
    }
  };

  const handleDownload = async (selectedIds, allItems) => {
    if (selectedIds.length === 0) {
      alert("리뷰를 선택해주세요");
      return;
    }
    try {
      setIsDownloading(true);
      const selectedRows = allItems.filter((item) =>
        selectedIds.includes(item[adReviewDeleteConfig.table.keyField])
      );
      await download(adReviewDeleteConfig, selectedRows, allItems);
      alert("다운로드가 완료되었습니다.");
    } catch (error) {
      console.error("다운로드 오류:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  const transformedSummary = {
    totalCount: summary.total || 0,
    waitingCount: summary.waiting || 0,
    processingCount: summary.processing || 0,
    completedCount: summary.completed || 0,
    partiallyRefundedCount: summary.partiallyRefunded || 0,
    refundedCount: summary.refunded || 0,
  };

  const configWithRowClick = {
    ...adReviewDeleteConfig,
    table: {
      ...adReviewDeleteConfig.table,
      rowClick: { enabled: true, handler: handleRowClick },
    },
  };

  return (
    <Layout>
      <CampaignPageLayout
        config={configWithRowClick}
        data={{ items, summary: transformedSummary }}
        filter={filters.filter}
        selectedIds={selection.selectedIds}
        loading={loading || isDownloading}
        handlers={{
          tempFilter: filters.tempFilter,
          onFilterChange: handleFilterChange,
          onFilterApply: handleFilterApply,
          onFilterReset: handleFilterReset,
          onSelect: handleSelect,
          onSelectAll: handleSelectAll,
          onPageChange: handlePageChange,
          onAction: handleAction,
          onDownload: handleDownload,
          openReportModal: openReportModal,
          onRefundOpen: handleRefundOpen,
        }}
      />

      {/* 상세 팝업 */}
      {isPopupOpen && selectedReview && (
        <div className="popup-overlay" onClick={handleClosePopup}>
          <div
            className="popup-content"
            style={{ maxWidth: "860px", width: "95%" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="popup-header">
              <h3>리뷰 삭제 상세 정보</h3>
              <button className="close-button" onClick={handleClosePopup}>×</button>
            </div>

            <div className="popup-body">
              {/* 신청 정보 */}
              <div>
                <div className="detail-section-title">신청 정보</div>
                <div className="detail-info-grid">
                  <div className="detail-info-item">
                    <span className="detail-info-label">사업자명</span>
                    <span className="detail-info-value">{selectedReview.placeName}</span>
                  </div>
                  <div className="detail-info-item">
                    <span className="detail-info-label">신청자</span>
                    <span className="detail-info-value">
                      {selectedReview.userName} ({selectedReview.userId})
                    </span>
                  </div>
                  <div className="detail-info-item">
                    <span className="detail-info-label">결제방법</span>
                    <span className="detail-info-value">
                      {getPaymentLabel(selectedReview.paymentType)}
                    </span>
                  </div>
                  <div className="detail-info-item">
                    <span className="detail-info-label">결제금액</span>
                    <span className="detail-info-value">
                      {(selectedReview.totalCost ?? 0).toLocaleString()}원
                    </span>
                  </div>
                  <div className="detail-info-item">
                    <span className="detail-info-label">환불금액</span>
                    <span className="detail-info-value">
                      {(selectedReview.refundedAmount ?? 0).toLocaleString()}원
                    </span>
                  </div>
                  <div className="detail-info-item">
                    <span className="detail-info-label">상태</span>
                    <span className="detail-info-value">
                      <span className={`badge badge-${selectedReview.status}`}>
                        {getStatusLabel(selectedReview.status)}
                      </span>
                    </span>
                  </div>
                </div>
              </div>

              {/* URL 목록 */}
              <div>
                <div className="detail-section-title">
                  URL 목록
                  <span className="detail-section-count">
                    {(selectedReview.urls || []).length}건
                  </span>
                </div>
                <table className="detail-url-table">
                  <thead>
                    <tr>
                      <th>URL</th>
                      <th style={{ width: "100px" }}>Campaign ID</th>
                      <th style={{ width: "80px" }}>상태</th>
                      <th style={{ width: "80px" }}>환불액</th>
                      <th style={{ width: "80px" }}>접수</th>
                      <th style={{ width: "90px" }}>처리완료</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selectedReview.urls || []).map((url) => {
                      const safeHref = toExternalUrl(url.reviewUrl);
                      return (
                      <tr key={url.adReviewDeleteId}>
                        <td>
                          {safeHref ? (
                            <a
                              className="url-link"
                              href={safeHref}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {url.reviewUrl}
                            </a>
                          ) : (
                            <span className="url-link">{url.reviewUrl}</span>
                          )}
                        </td>
                        <td>{url.adReviewDeleteId}</td>
                        <td>
                          <span className={`badge badge-${url.status}`}>
                            {getStatusLabel(url.status)}
                          </span>
                        </td>
                        <td>{(url.refundedAmount ?? 0).toLocaleString()}원</td>
                        <td>
                          <button
                            type="button"
                            className={`detail-action-btn btn-submit${url.isSubmitted ? " active" : ""}`}
                            onClick={() => handleUrlAction("submitted", url.adReviewDeleteId)}
                            disabled={!!urlActionLoading[url.adReviewDeleteId] || url.status === "refunded"}
                          >
                            {url.isSubmitted ? "접수취소" : "접수"}
                          </button>
                        </td>
                        <td>
                          <button
                            type="button"
                            className={`detail-action-btn btn-complete${url.isCompleted ? " active" : ""}`}
                            onClick={() => handleUrlAction("complete", url.adReviewDeleteId)}
                            disabled={!!urlActionLoading[url.adReviewDeleteId] || url.status === "refunded"}
                          >
                            {url.isCompleted ? "처리취소" : "처리완료"}
                          </button>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* 첨부자료 */}
              {(selectedReview.businessRegistrationUrl ||
                (selectedReview.reviewImgUrls?.length > 0)) && (
                <div>
                  <div className="detail-section-title">첨부자료</div>
                  <div className="detail-attach-grid">
                    {selectedReview.businessRegistrationUrl && (
                      <div className="detail-attach-item">
                        <span className="detail-attach-label">사업자등록증</span>
                        <img
                          className="detail-attach-thumb"
                          src={selectedReview.businessRegistrationUrl}
                          alt="사업자등록증"
                        />
                      </div>
                    )}
                    {(selectedReview.reviewImgUrls || []).map((imgUrl, i) => (
                      <div className="detail-attach-item" key={i}>
                        <span className="detail-attach-label">악성리뷰 {i + 1}</span>
                        <img
                          className="detail-attach-thumb"
                          src={imgUrl}
                          alt={`악성리뷰 ${i + 1}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {reportMetaId && (
        <ReportUrlModal
          adCampaignMetaId={reportMetaId}
          placeName={reportPlaceName}
          onClose={() => { setReportMetaId(null); setReportPlaceName(null); }}
          onSaved={refresh}
        />
      )}

      <NotificationModal
        isOpen={notificationData !== null}
        adCampaignMetaId={notificationData?.adCampaignMetaId}
        userId={notificationData?.userId}
        productName={notificationData?.productName}
        submittedAt={notificationData?.submittedAt}
        purchaseAmount={notificationData?.purchaseAmount}
        onClose={() => setNotificationData(null)}
        onSuccess={refresh}
      />

      <RefundModal
        isOpen={refundModalData !== null}
        items={refundModalData?.items || []}
        keyField="adReviewDeleteId"
        showResultStep
        onClose={() => setRefundModalData(null)}
        onSubmit={handleRefundSubmit}
      />

      <MemoModal
        isOpen={memoEdit.isOpen}
        id={memoEdit.memoData?.id}
        currentMemo={memoEdit.memoData?.currentMemo}
        loading={memoEdit.loading}
        onSave={memoEdit.save}
        onDelete={memoEdit.deleteMemo}
        onClose={memoEdit.closeModal}
      />
    </Layout>
  );
}

export default AdReviewDelete;
